import { useEffect, useState } from 'react';
import { isNotFound } from '@localagent/sdk';
import { getSDK } from '../../services/sdk-instance';
import { ImageLightbox } from './ImageLightbox';
import { ensureStandaloneSvg, loadMediaPreview, previewFormatFor, type MediaPreviewFormat } from './media-preview';

const HANDLE_RE =
  /media:\/\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})@v(\d+)/gi;

export interface ParsedMediaHandle {
  raw: string;
  id: string;
  version?: number;
}

export function parseMediaHandles(text: string): ParsedMediaHandle[] {
  const found: ParsedMediaHandle[] = [];
  const seen = new Set<string>();
  for (const match of text.matchAll(HANDLE_RE)) {
    const raw = match[0];
    if (seen.has(raw)) continue;
    seen.add(raw);
    const versionRaw = match[2];
    found.push({
      raw,
      id: match[1],
      version: versionRaw === undefined ? undefined : Number(versionRaw),
    });
  }
  return found;
}

export type MessageDiagramViewState =
  | { kind: 'loading'; width?: number; height?: number }
  | {
      kind: 'ready';
      width: number;
      height: number;
      previewUrl: string;
      previewKind: MediaPreviewFormat;
      warnings: string[];
      label: string;
    }
  | { kind: 'failed'; message: string }
  | { kind: 'tombstone' };

export interface MessageDiagramViewProps {
  state: MessageDiagramViewState;
  pinnedVersion?: number;
  downloadError?: string;
  onDownload?: () => void;
  onPreviewError?: () => void;
}

function DownloadIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 15V3" />
      <path d="m7 10 5 5 5-5" />
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    </svg>
  );
}

function ExpandIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M15 3h6v6" />
      <path d="M9 21H3v-6" />
      <path d="m21 3-7 7" />
      <path d="m3 21 7-7" />
    </svg>
  );
}

const ICON_BUTTON =
  'inline-flex h-7 items-center gap-1 rounded-md px-1.5 text-caption text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

export function MessageDiagramView({
  state,
  pinnedVersion,
  downloadError,
  onDownload,
  onPreviewError,
}: MessageDiagramViewProps) {
  const [expanded, setExpanded] = useState(false);
  const reserved =
    (state.kind === 'loading' || state.kind === 'ready') &&
    state.width !== undefined &&
    state.height !== undefined
      ? { width: state.width, height: state.height }
      : undefined;
  const title = state.kind === 'ready' ? state.label : 'Diagram';

  return (
    <figure
      data-testid="message-diagram"
      data-diagram-state={state.kind}
      data-diagram-version={pinnedVersion === undefined ? undefined : String(pinnedVersion)}
      data-has-warnings={state.kind === 'ready' && state.warnings.length > 0 ? 'true' : 'false'}
      data-preview-kind={state.kind === 'ready' ? state.previewKind : undefined}
      className="mt-3 w-fit min-w-[16rem] max-w-full overflow-hidden rounded-lg bg-card shadow-sm"
    >
      <figcaption className="flex items-center gap-2 py-1.5 pl-3 pr-1.5">
        <span className="min-w-0 truncate text-label text-foreground" data-testid="message-diagram-title">
          {title}
        </span>
        <span className="shrink-0 text-micro text-muted-foreground">
          Diagram{pinnedVersion !== undefined ? ` · v${pinnedVersion}` : ''}
        </span>
        <span className="ml-auto flex shrink-0 items-center gap-0.5">
          {onDownload ? (
            <button
              type="button"
              data-testid="message-diagram-download"
              className={ICON_BUTTON}
              aria-label="Download"
              title="Download"
              onClick={onDownload}
            >
              <DownloadIcon />
            </button>
          ) : null}
          {state.kind === 'ready' ? (
            <button
              type="button"
              data-testid="message-diagram-expand"
              className={ICON_BUTTON}
              aria-label="Expand"
              title="Expand"
              onClick={() => setExpanded(true)}
            >
              <ExpandIcon />
            </button>
          ) : null}
        </span>
      </figcaption>

      {downloadError ? (
        <div data-testid="message-diagram-download-error" className="px-3 pb-1.5 text-caption text-error-text">
          {downloadError}
        </div>
      ) : null}

      {state.kind === 'tombstone' ? (
        <div data-testid="message-diagram-tombstone" className="px-3 pb-3 text-body text-muted-foreground">
          This artifact is in Trash or has been deleted.
        </div>
      ) : null}

      {state.kind === 'failed' ? (
        <div data-testid="message-diagram-failed" className="px-3 pb-3 text-body text-error-text" role="alert">
          {state.message}
        </div>
      ) : null}

      {state.kind === 'loading' ? (
        <div className="mx-2 mb-2 rounded-md bg-sunken p-2">
          <div data-testid="message-diagram-loading" className="max-w-full bg-transparent" style={reserved} aria-busy="true" />
        </div>
      ) : null}

      {state.kind === 'ready' ? (
        <>
          {state.warnings.length > 0 ? (
            <div data-testid="message-diagram-warning" className="px-3 pb-1.5 text-caption text-muted-foreground">
              This drawing contains interactivity that will not run here:{' '}
              {state.warnings.join(', ')}. Download the file to open it elsewhere.
            </div>
          ) : null}
          <div className="mx-2 mb-2 rounded-md bg-sunken p-2">
            <button
              type="button"
              className="block cursor-zoom-in rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label={`Expand ${state.label}`}
              onClick={() => setExpanded(true)}
            >
              <img
                data-testid="message-diagram-preview"
                src={state.previewUrl}
                alt={state.label}
                width={state.width}
                height={state.height}
                className="block h-auto max-h-[34rem] w-auto max-w-full rounded bg-transparent object-contain"
                style={{ filter: 'none' }}
                onError={onPreviewError}
              />
            </button>
          </div>
          <ImageLightbox
            open={expanded}
            onOpenChange={setExpanded}
            src={state.previewUrl}
            label={state.label}
            testId="message-diagram-lightbox"
          />
        </>
      ) : null}
    </figure>
  );
}

function filenameFor(label: string, suffix: string): string {
  const stem = label.replace(/[^A-Za-z0-9._-]+/g, '-').replace(/^-+|-+$/g, '');
  if (stem.length === 0) {
    throw new Error('artifact label produced an empty download name');
  }
  return `${stem}-${suffix}`;
}

function triggerDownload(bytes: ArrayBuffer, filename: string, mime: string): void {
  const blob = new Blob([bytes], { type: mime });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

export function MessageDiagram({ handle }: { handle: ParsedMediaHandle }) {
  const [state, setState] = useState<MessageDiagramViewState>({ kind: 'loading' });
  const [downloadError, setDownloadError] = useState<string | undefined>(undefined);
  const [label, setLabel] = useState('diagram');

  useEffect(() => {
    let cancelled = false;
    let previewUrl: string | undefined;
    setState({ kind: 'loading' });

    const load = async () => {
      try {
        const sdk = getSDK();
        const object = await sdk.media.get(handle.id);
        if (cancelled) return;
        if (object.deleted_at) {
          setState({ kind: 'tombstone' });
          return;
        }
        const versions = object.versions;
        if (!versions || versions.length === 0) {
          throw new Error('media object has no versions');
        }
        if (handle.version === undefined) {
          throw new Error('chat media handle omitted a version');
        }
        const pinned = handle.version;
        const version = versions.find((row) => row.version === pinned);
        if (!version) {
          throw new Error(`media object has no version ${pinned}`);
        }
        if (version.width === undefined || version.height === undefined) {
          throw new Error(`media version ${pinned} is missing width or height`);
        }
        setLabel(object.label);
        setState({ kind: 'loading', width: version.width, height: version.height });
        if (!version.formats) {
          throw new Error(`media version ${pinned} came back without its formats`);
        }
        const format = previewFormatFor(
          version.formats.map((f) => f.format),
          version.warnings,
        );
        if (!format) {
          throw new Error(`media version ${pinned} has no previewable format`);
        }
        const url = await loadMediaPreview(handle.id, format, pinned);
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        previewUrl = url;
        setState({
          kind: 'ready',
          width: version.width,
          height: version.height,
          previewUrl,
          previewKind: format,
          warnings: version.warnings,
          label: object.label,
        });
      } catch (error) {
        if (cancelled) return;
        if (isNotFound(error)) {
          setState({ kind: 'tombstone' });
          return;
        }
        const message = error instanceof Error ? error.message : String(error);
        setState({ kind: 'failed', message });
      }
    };

    void load();
    return () => {
      cancelled = true;
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [handle.id, handle.version]);

  const download = async (version: number, suffix: string) => {
    setDownloadError(undefined);
    try {
      const raw = await getSDK().media.content(handle.id, { format: 'svg', version });
      const bytes = ensureStandaloneSvg(raw);
      triggerDownload(bytes, filenameFor(label, suffix), 'image/svg+xml');
    } catch (error) {
      if (isNotFound(error)) {
        setDownloadError('This artifact is in Trash or has been deleted.');
        return;
      }
      setDownloadError(error instanceof Error ? error.message : String(error));
    }
  };

  const pinned = handle.version;

  return (
    <MessageDiagramView
      state={state}
      pinnedVersion={pinned}
      downloadError={downloadError}
      onPreviewError={() => {
        setState({ kind: 'failed', message: 'diagram preview could not be displayed' });
      }}
      onDownload={
        pinned === undefined
          ? undefined
          : () => {
              void download(pinned, `v${pinned}.svg`);
            }
      }
    />
  );
}

export function MessageDiagrams({ text }: { text: string }) {
  const handles = parseMediaHandles(text);
  if (handles.length === 0) return null;
  return (
    <div data-testid="message-diagrams" className="flex flex-col gap-2">
      {handles.map((handle) => (
        <MessageDiagram key={handle.raw} handle={handle} />
      ))}
    </div>
  );
}
